import { cleanPermalink } from '../../../statistics/lib/top-videos';
import { cellNumber } from '../post-rows';
import { zipStore, type ZipEntry } from './zip';

/**
 * Отчёт-«посев» в Excel: строка на публикацию, экран статистики картинкой
 * прямо в ячейке.
 *
 * ПОЧЕМУ СВОЙ ПИСАТЕЛЬ. Формат задан образцом клиента (Askar Cinema):
 * ширины колонок, высота строки под превью, скрин внутри таблицы. Готовая
 * библиотека в проекте одна — SheetJS, и картинки в ячейки она не кладёт.
 * Поэтому книга собирается руками: это несколько xml по спецификации
 * OOXML и zip вокруг них (см. zip.ts).
 *
 * ЧТО В КНИГЕ. Один лист, без итогов: «посев» показывают как перечень
 * размещений, суммы клиент считает сам. Первая строка закреплена, ссылка
 * кликается, просмотры и охваты — числа с разрядами, а не текст, чтобы по
 * ним можно было отсортировать и просуммировать.
 */

export type XlsxImage = {
  data: Uint8Array;
  /** Натуральный размер картинки: по нему считается ширина в ячейке. */
  width: number;
  height: number;
  type: 'jpg' | 'png';
};

export type SeedingRow = {
  /** Паблик: «@kazpress». */
  username: string;
  format: string;
  url: string;
  /** Цифры приходят строками из таблицы на странице, как их правит человек. */
  views: string;
  reach: string;
  screen: XlsxImage | null;
};

export type SeedingBook = {
  /** Имя листа — обычно клиент или кампания. */
  sheet: string;
  rows: SeedingRow[];
};

/** Колонки и их ширина в знаках — как в образце клиента. */
const COLUMNS = [
  { title: '№', width: 5 },
  { title: 'Паблик', width: 20 },
  { title: 'Формат', width: 10 },
  { title: 'Ссылка', width: 46 },
  { title: 'Скрин', width: 17.25 },
  { title: 'Просмотры', width: 12 },
  { title: 'Охваты', width: 12 },
] as const;

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G'] as const;
/** Колонка «Скрин», считая от нуля: в неё встаёт картинка. */
const SCREEN_COLUMN = 4;

/** Высота строки в пунктах — 2,25 см, как в образце. */
const ROW_HEIGHT = 63.75;
/** Превью ровно в высоту строки: 85 px = 63,75 pt. */
const SHOT_HEIGHT_PX = 85;
/** Excel меряет картинки в EMU: 914 400 на дюйм, 96 точек на дюйм. */
const EMU_PER_PX = 9525;

/** Стили ячеек: номера совпадают с порядком xf в styles.xml. */
const STYLE = {
  header: 1,
  center: 2,
  left: 3,
  link: 4,
  number: 5,
} as const;

const XML_HEAD = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Имя листа по правилам Excel: до 31 знака и без : \ / ? * [ ].
 * Пустое имя книга не принимает вовсе, поэтому у него есть запасное.
 */
export function sheetTitle(name: string): string {
  const clean = name.replace(/[:\\/?*[\]]/g, ' ').trim().slice(0, 31);
  return clean || 'Посев';
}

function text(value: string): string {
  return `<is><t xml:space="preserve">${escapeXml(value)}</t></is>`;
}

function cell(ref: string, style: number, body: string, type = ''): string {
  return `<c r="${ref}" s="${style}"${type}>${body}</c>`;
}

/** Ячейка с цифрой: число — числом, всё остальное остаётся текстом. */
function numberCell(ref: string, value: string): string {
  const parsed = cellNumber(value);
  if (parsed !== null) return cell(ref, STYLE.number, `<v>${parsed}</v>`);
  if (!value.trim()) return '';
  return cell(ref, STYLE.number, text(value), ' t="inlineStr"');
}

function sheetXml(rows: SeedingRow[], links: Array<{ ref: string; id: string }>): string {
  const cols = COLUMNS.map(
    (column, i) => `<col min="${i + 1}" max="${i + 1}" width="${column.width}" customWidth="1"/>`,
  ).join('');

  const header = COLUMNS.map((column, i) =>
    cell(`${LETTERS[i]}1`, STYLE.header, text(column.title), ' t="inlineStr"'),
  ).join('');

  const body = rows
    .map((row, i) => {
      const r = i + 2;
      const cells = [
        cell(`A${r}`, STYLE.center, `<v>${i + 1}</v>`),
        cell(`B${r}`, STYLE.left, text(row.username), ' t="inlineStr"'),
        cell(`C${r}`, STYLE.center, text(row.format), ' t="inlineStr"'),
        row.url ? cell(`D${r}`, STYLE.link, text(cleanPermalink(row.url)), ' t="inlineStr"') : '',
        numberCell(`F${r}`, row.views),
        numberCell(`G${r}`, row.reach),
      ].join('');
      return `<row r="${r}" spans="1:7" ht="${ROW_HEIGHT}" customHeight="1">${cells}</row>`;
    })
    .join('');

  const hyperlinks = links.length
    ? `<hyperlinks>${links
        .map((link) => `<hyperlink ref="${link.ref}" r:id="${link.id}"/>`)
        .join('')}</hyperlinks>`
    : '';

  return (
    `${XML_HEAD}<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"` +
    ' xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
    `<dimension ref="A1:G${rows.length + 1}"/>` +
    '<sheetViews><sheetView workbookViewId="0">' +
    // Шапка закреплена: списки длинные, и колонки надо видеть на любой строке.
    '<pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>' +
    '<selection pane="bottomLeft" activeCell="A2" sqref="A2"/>' +
    '</sheetView></sheetViews>' +
    '<sheetFormatPr defaultRowHeight="15"/>' +
    `<cols>${cols}</cols>` +
    `<sheetData><row r="1" spans="1:7">${header}</row>${body}</sheetData>` +
    hyperlinks +
    '<pageMargins left="0.7" right="0.7" top="0.75" bottom="0.75" header="0.3" footer="0.3"/>' +
    '<drawing r:id="rIdDrawing"/></worksheet>'
  );
}

/** Картинка в ячейке «Скрин»: свой якорь на каждую строку, где экран есть. */
type Anchor = { row: number; relId: string; screen: XlsxImage };

function drawingXml(anchors: Anchor[]): string {
  const shapes = anchors
    .map((anchor, order) => {
      const ratio = anchor.screen.width / anchor.screen.height;
      const cx = Math.round(SHOT_HEIGHT_PX * ratio) * EMU_PER_PX;
      const cy = SHOT_HEIGHT_PX * EMU_PER_PX;
      const id = order + 1;
      return (
        '<xdr:oneCellAnchor>' +
        `<xdr:from><xdr:col>${SCREEN_COLUMN}</xdr:col><xdr:colOff>0</xdr:colOff>` +
        `<xdr:row>${anchor.row + 1}</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:from>` +
        `<xdr:ext cx="${cx}" cy="${cy}"/>` +
        '<xdr:pic><xdr:nvPicPr>' +
        `<xdr:cNvPr id="${id}" name="Экран ${id}"/>` +
        '<xdr:cNvPicPr><a:picLocks noChangeAspect="1"/></xdr:cNvPicPr></xdr:nvPicPr>' +
        `<xdr:blipFill><a:blip r:embed="${anchor.relId}"/><a:stretch><a:fillRect/></a:stretch></xdr:blipFill>` +
        `<xdr:spPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="${cx}" cy="${cy}"/></a:xfrm>` +
        '<a:prstGeom prst="rect"><a:avLst/></a:prstGeom></xdr:spPr></xdr:pic>' +
        '<xdr:clientData/></xdr:oneCellAnchor>'
      );
    })
    .join('');

  return (
    `${XML_HEAD}<xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing"` +
    ' xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"' +
    ' xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
    `${shapes}</xdr:wsDr>`
  );
}

const STYLES_XML =
  `${XML_HEAD}<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
  '<numFmts count="1"><numFmt numFmtId="164" formatCode="# ##0"/></numFmts>' +
  '<fonts count="3">' +
  '<font><sz val="11"/><color theme="1"/><name val="Calibri"/><family val="2"/><scheme val="minor"/></font>' +
  '<font><b/><sz val="11"/><color theme="1"/><name val="Calibri"/><family val="2"/><scheme val="minor"/></font>' +
  '<font><u/><sz val="11"/><color theme="10"/><name val="Calibri"/><family val="2"/><scheme val="minor"/></font>' +
  '</fonts>' +
  '<fills count="2"><fill><patternFill patternType="none"/></fill>' +
  '<fill><patternFill patternType="gray125"/></fill></fills>' +
  '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
  '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
  '<cellXfs count="6">' +
  '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>' +
  '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1">' +
  '<alignment horizontal="center" vertical="center"/></xf>' +
  '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" applyAlignment="1">' +
  '<alignment horizontal="center" vertical="center"/></xf>' +
  '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" applyAlignment="1">' +
  '<alignment vertical="center"/></xf>' +
  '<xf numFmtId="0" fontId="2" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1">' +
  '<alignment vertical="center"/></xf>' +
  '<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyAlignment="1">' +
  '<alignment horizontal="right" vertical="center"/></xf>' +
  '</cellXfs>' +
  '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
  '<dxfs count="0"/></styleSheet>';

const RELS = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';

function relationships(items: Array<{ id: string; type: string; target: string; external?: boolean }>): string {
  const list = items
    .map(
      (item) =>
        `<Relationship Id="${item.id}" Type="${RELS}/${item.type}" Target="${escapeXml(item.target)}"` +
        `${item.external ? ' TargetMode="External"' : ''}/>`,
    )
    .join('');
  return `${XML_HEAD}<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${list}</Relationships>`;
}

const DOC_TYPES = 'application/vnd.openxmlformats-officedocument';

function contentTypes(extensions: string[]): string {
  const defaults = ['rels', 'xml', ...extensions]
    .map((ext) => {
      const type =
        ext === 'rels'
          ? 'application/vnd.openxmlformats-package.relationships+xml'
          : ext === 'xml'
            ? 'application/xml'
            : `image/${ext}`;
      return `<Default Extension="${ext}" ContentType="${type}"/>`;
    })
    .join('');
  return (
    `${XML_HEAD}<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">${defaults}` +
    `<Override PartName="/xl/workbook.xml" ContentType="${DOC_TYPES}.spreadsheetml.sheet.main+xml"/>` +
    `<Override PartName="/xl/worksheets/sheet1.xml" ContentType="${DOC_TYPES}.spreadsheetml.worksheet+xml"/>` +
    `<Override PartName="/xl/styles.xml" ContentType="${DOC_TYPES}.spreadsheetml.styles+xml"/>` +
    `<Override PartName="/xl/drawings/drawing1.xml" ContentType="${DOC_TYPES}.drawing+xml"/>` +
    '</Types>'
  );
}

function utf8(value: string): Uint8Array<ArrayBuffer> {
  return new TextEncoder().encode(value);
}

/** Книга целиком: готовые байты .xlsx. */
export function buildSeedingXlsx({ sheet, rows }: SeedingBook): Uint8Array<ArrayBuffer> {
  const links = rows
    .map((row, i) => ({ ref: `D${i + 2}`, id: `rId${i + 1}`, url: cleanPermalink(row.url) }))
    .filter((link) => link.url);

  // Картинки нумеруются подряд по тем строкам, где экран есть: у строки
  // без скриншота ячейка просто остаётся пустой.
  const images = rows
    .flatMap((row, index) => (row.screen ? [{ screen: row.screen, row: index }] : []))
    .map((item, order) => ({
      ...item,
      relId: `rId${order + 1}`,
      name: `image${order + 1}.${item.screen.type === 'png' ? 'png' : 'jpeg'}`,
    }));

  const extensions = [...new Set(images.map((image) => image.name.split('.')[1]))];

  const entries: ZipEntry[] = [
    { path: '[Content_Types].xml', data: utf8(contentTypes(extensions)) },
    {
      path: '_rels/.rels',
      data: utf8(
        relationships([{ id: 'rId1', type: 'officeDocument', target: 'xl/workbook.xml' }]),
      ),
    },
    {
      path: 'xl/workbook.xml',
      data: utf8(
        `${XML_HEAD}<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"` +
          ` xmlns:r="${RELS}"><sheets>` +
          `<sheet name="${escapeXml(sheetTitle(sheet))}" sheetId="1" r:id="rId1"/>` +
          '</sheets></workbook>',
      ),
    },
    {
      path: 'xl/_rels/workbook.xml.rels',
      data: utf8(
        relationships([
          { id: 'rId1', type: 'worksheet', target: 'worksheets/sheet1.xml' },
          { id: 'rId2', type: 'styles', target: 'styles.xml' },
        ]),
      ),
    },
    { path: 'xl/styles.xml', data: utf8(STYLES_XML) },
    { path: 'xl/worksheets/sheet1.xml', data: utf8(sheetXml(rows, links)) },
    {
      path: 'xl/worksheets/_rels/sheet1.xml.rels',
      data: utf8(
        relationships([
          ...links.map((link) => ({
            id: link.id,
            type: 'hyperlink',
            target: link.url,
            external: true,
          })),
          { id: 'rIdDrawing', type: 'drawing', target: '../drawings/drawing1.xml' },
        ]),
      ),
    },
    { path: 'xl/drawings/drawing1.xml', data: utf8(drawingXml(images)) },
    {
      path: 'xl/drawings/_rels/drawing1.xml.rels',
      data: utf8(
        relationships(
          images.map((image) => ({
            id: image.relId,
            type: 'image',
            target: `../media/${image.name}`,
          })),
        ),
      ),
    },
    ...images.map((image) => ({ path: `xl/media/${image.name}`, data: image.screen.data })),
  ];

  return zipStore(entries);
}
